import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Link2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  const [mergePrompt, setMergePrompt] = useState(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    const sessionId = hash?.split('session_id=')[1];

    if (!sessionId) {
      setError('No session data received from Google');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/google/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId })
    })
      .then(res => res.json())
      .then(data => {
        if (data.requiresSignup) {
          sessionStorage.setItem('sso_signup_data', JSON.stringify({
            email: data.email,
            name: data.name,
            provider: 'google',
            ssoId: data.ssoId,
            picture: data.picture
          }));
          navigate('/sso-complete');
        } else if (data.needsMerge) {
          setMergePrompt({
            token: data.token,
            provider: data.provider,
            email: data.email,
            name: data.name
          });
        } else if (data.token) {
          loginWithToken(data.token, data.user);
          navigate('/');
        } else {
          throw new Error(data.detail || 'Authentication failed');
        }
      })
      .catch(err => {
        console.error('Google SSO error:', err);
        setError(err.message || 'Google authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      });
  }, []);

  const handleConfirmMerge = async () => {
    setMerging(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/confirm-merge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${mergePrompt.token}`
        },
        body: JSON.stringify({ provider: mergePrompt.provider })
      });

      if (!res.ok) throw new Error('Failed to link account');

      const data = await res.json();
      loginWithToken(mergePrompt.token, data.user);
      navigate('/');
    } catch (err) {
      setError('Failed to link accounts. Please try again.');
      setTimeout(() => navigate('/login'), 3000);
    } finally {
      setMerging(false);
    }
  };

  const handleDeclineMerge = () => {
    navigate('/login');
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error}</p>
          <p className="text-slate-400 mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (mergePrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8" data-testid="merge-account-dialog">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Link2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>

          <h2 className="text-xl font-bold text-center text-slate-900 dark:text-white mb-2">
            Link Google Account?
          </h2>

          <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
            An Inspiro Tracker account already exists for <strong className="text-slate-700 dark:text-slate-200">{mergePrompt.email}</strong>. 
            Would you like to link your Google account so you can sign in with either method?
          </p>

          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Your existing data, projects, and settings will remain unchanged. This only adds Google as a sign-in option.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={handleConfirmMerge}
              disabled={merging}
              className="w-full"
              data-testid="confirm-merge-btn"
            >
              {merging ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Linking...
                </>
              ) : (
                'Yes, link Google account'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleDeclineMerge}
              disabled={merging}
              className="w-full"
              data-testid="decline-merge-btn"
            >
              No, go back to login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
        <p className="text-slate-600 dark:text-slate-300 mt-4">Authenticating with Google...</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
